import React, { useEffect, useMemo, useState} from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import './themeSwitch.css';
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Stack,
  Chip,
  Button,
  keyframes,
  Typography,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Star as StarIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  ListAlt as ListAltIcon,
  Psychology as PsychologyIcon,
  History as HistoryIcon,
  // HelpOutline as HelpOutlineIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  VideoLibrary as VideoLibraryIcon,
  InfoOutlined as InfoOutlinedIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  CardMembership as CardMembershipIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/TranslationContext';
// import { useGlobalTour } from '../../contexts/TourContext';
import { useGlobalVideo } from '../../contexts/VideoContext';
import { getSubscriptionInfo } from '../../services/api';
import { formatPersianNumber } from '../../utils/validation';

const drawerWidth = 240;
const collapsedDrawerWidth = 70;

const getSubscriptionColor = (plan?: string) => {
  switch (plan) {
    case 'intern':
      return '#1976d2'; // Blue
    case 'expert':
      return '#ff9800'; // Golden
    case 'master':
      return '#009688'; // Cyan
    case 'business':
      return '#E53935'; // Gray
    default:
      return '#757575'; // Gray
  }
};

interface SubscriptionData {
  plan: 'intern' | 'expert' | 'master';
  duration: 'quarterly' | 'semiannual' | 'annual';
  subscription_type: 'seller' | 'assistant';
  amount: number;
  start_date: string;
  expire_date: string;
  days_remaining: number;
}

const blink = keyframes`
  0% { background-color: rgba(255, 152, 0, 0.1); }
  50% { background-color: rgba(255, 152, 0, 0.3); }
  100% { background-color: rgba(255, 152, 0, 0.1); }
`;

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { t, currentLanguage } = useTranslation();
  // const { startTour } = useGlobalTour();
  const { startVideo } = useGlobalVideo();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Helper function to check if a route is active
  const isRouteActive = (path: string) => {
    return location.pathname === path;
  };
  
  // Check if user is verified
  const isUserVerified = user?.user?.is_verified;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isDrawerHovered, setIsDrawerHovered] = React.useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = React.useState(false);
  const hoverTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const switchRef = React.useRef<HTMLDivElement>(null);


  useEffect(() => {
    const switchBody = switchRef.current;
    
    if (switchBody) {
      const toggle = () => {
        switchBody.classList.toggle("off");
      };
      
      // Add event listener only once
      switchBody.addEventListener("click", toggle);
      
      // Cleanup function to remove event listener
      return () => {
        switchBody.removeEventListener("click", toggle);
      };
    }
  }, []);
  
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await getSubscriptionInfo();
        if (response.has_active_subscription && response.data) {
          setSubscription(response.data);
        } else {
          setSubscription(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setSubscription(null);
      }
    };
    fetchSubscription();
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerMouseEnter = () => {
    // Set a timer to expand drawer after 500ms
    hoverTimerRef.current = setTimeout(() => {
      setIsDrawerHovered(true);
    }, 100);
  };

  const handleDrawerMouseLeave = () => {
    // Clear the timer if it exists
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // Immediately collapse the drawer
    setIsDrawerHovered(false);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/auth/auth');
  };

  // const handleHelpButtonClick = () => {
  //   // Only start dashboard tour if we're on the dashboard page
  //   if (location.pathname === '/dashboard') {
  //     startTour();
  //   } else {
  //     // For other pages, use the existing tour functionality
  //     startTour();
  //   }
  // };

  const handleVideoButtonClick = () => {
    // Start video for current page
    startVideo();
  };

  const isRtl = currentLanguage === 'fa';

  type UiModuleKey = 'dashboard' | 'settings' | 'histories' | 'profiles' | 'knowledge' | 'chatbox' | 'membership';

  const isMemberUser = user?.membership?.is_member === true;
  const allowedUiModules = user?.membership?.ui_modules ?? [];

  const canShowModule = (module: UiModuleKey): boolean => {
    if (!isMemberUser) return true;
    return allowedUiModules.includes(module);
  };

  const dashboardItem = {
    text: t('menu.dashboard'),
    icon: <DashboardIcon />,
    path: '/dashboard'
  };

  const showDashboard = canShowModule('dashboard');

  const menuItems = useMemo(
    () =>
      [
        {
          module: 'settings' as const,
          text: 'تنظیمات آیدا',
          icon: <PsychologyIcon sx={{ color: 'black', width: '24px', height: '24px' }} />,
          path: '/aida-settings',
        },
        {
          module: 'histories' as const,
          text: 'گفتگو ها',
          icon: <HistoryIcon sx={{ color: 'black', width: '24px', height: '24px' }} />,
          path: '/conversations',
        },
        {
          module: 'profiles' as const,
          text: 'پروفایل',
          icon: <img src="/images/profile.svg" alt="" style={{ width: '24px', height: '24px' }} />,
          path: '/profile-and-business',
        },
        {
          module: 'knowledge' as const,
          text: 'دانش آیدا',
          icon: <ListAltIcon sx={{ color: 'black', width: '24px', height: '24px' }} />,
          path: '/knowledge-base',
        },
        {
          module: 'chatbox' as const,
          text: 'چت باکس',
          icon: <ChatBubbleOutlineIcon sx={{ color: 'black', width: '24px', height: '24px' }} />,
          path: '/chatbox',
        },
        {
          module: 'membership' as const,
          text: 'مدیریت کاربران',
          icon: <CardMembershipIcon sx={{ color: 'black', width: '24px', height: '24px' }} />,
          path: '/membership',
        },
      ].filter((item) => {
        if (item.module === 'membership' && user?.onboarding?.subscription_plan !== 'business') {
          return false;
        }
        return canShowModule(item.module);
      }),
    [isMemberUser, allowedUiModules.join(','), user?.onboarding?.subscription_plan]
  );

  // Automate Response section items - REMOVED
  // const AutomateResponseItems = [
  //   { text: t('menu.automateResponseBot'), icon: <SmartToyIcon />, path: '/automate-response' },
  // ];

  const getRemainingDays = () => {
    return formatPersianNumber(subscription?.days_remaining ?? null);
  };

  // Determine if drawer should be expanded (either hovered or pinned)
  const isDrawerExpanded = isDrawerHovered || isDrawerPinned;

  // Drawer content for mobile (always expanded)
  const mobileDrawer = (
    <div>
      <Toolbar sx={{ justifyContent: 'center' , marginTop:'40px'}}>
        <img src="/images/aida-logo-ok.svg" alt="logo" style={{ width: '50px', height: '50px' }} />
      </Toolbar>
      <Divider />
      <List sx={{py:0}}>
        {/* Subscription Renewal Warning */}
        {user && user.user && user.user.is_verified && subscription?.plan === undefined && (
          <ListItem
            button
            onClick={() => {
              navigate('/onboarding/subscription');
              setMobileOpen(false);
            }}
            sx={{
              animation: `${blink} 2s infinite`,
              '&:hover': {
                animation: 'none',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '56px' }}>
              <WarningIcon color="warning" />
            </ListItemIcon>
            <ListItemText primary={t('menu.subscriptionRenewal')} />
          </ListItem>
        )}

        {/* Dashboard */}
        {showDashboard && (
          <ListItem
            button
            disabled={!isUserVerified}
            onClick={() => {
              if (isUserVerified) {
                navigate(dashboardItem.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              opacity: !isUserVerified ? 0.5 : 1,
              cursor: !isUserVerified ? 'not-allowed' : 'pointer',
              backgroundColor: isRouteActive(dashboardItem.path) ? '#e3f2fd' : 'transparent',
              '&:hover': {
                backgroundColor: isRouteActive(dashboardItem.path) ? '#bbdefb' : '#f5f5f5',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: '56px' }}>
              <img src="images/dashboard.svg" alt="" style={{ width: '24px', height: '24px' }} />
            </ListItemIcon>
            <ListItemText primary={dashboardItem.text} />
          </ListItem>
        )}

        {showDashboard && menuItems.length > 0 && <Divider />}

        {/* Menu Items */}
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            disabled={!isUserVerified}
            onClick={() => {
              if (isUserVerified) {
                navigate(item.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              opacity: !isUserVerified ? 0.5 : 1,
              cursor: !isUserVerified ? 'not-allowed' : 'pointer',
              backgroundColor: isRouteActive(item.path) ? '#e3f2fd' : 'transparent',
              '&:hover': {
                backgroundColor: isRouteActive(item.path) ? '#bbdefb' : '#f5f5f5',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: '56px' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}

      </List>
    </div>
  );

  // Drawer content for desktop (with hover effect)
  const drawer = (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px' }}>
        <img src="/images/aida-logo-ok.svg" alt="logo" style={{ width: '35px', height: '35px' }} />
      </Box>
      <Divider />
      <List sx={{py:0}}>
        {/* Subscription Renewal Warning */}
        {user && user.user && user.user.is_verified && subscription?.plan === undefined && (
          <ListItem
            button
            onClick={() => {
              navigate('/onboarding/subscription');
              setMobileOpen(false);
            }}
            sx={{
              animation: `${blink} 2s infinite`,
              '&:hover': {
                animation: 'none',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: isDrawerExpanded ? '56px' : '0', justifyContent: 'center' }}>
              <WarningIcon color="warning" />
            </ListItemIcon>
            <ListItemText 
              primary={t('menu.subscriptionRenewal')} 
              sx={{
                opacity: isDrawerExpanded ? 1 : 0,
                width: isDrawerExpanded ? 'auto' : 0,
                overflow: 'hidden',
                transition: 'opacity 0.3s ease, width 0.3s ease',
              }}
            />
          </ListItem>
        )}

        {/* Dashboard */}
        {showDashboard && (
          <ListItem
            button
            disabled={!isUserVerified}
            onClick={() => {
              if (isUserVerified) {
                navigate(dashboardItem.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              opacity: !isUserVerified ? 0.5 : 1,
              cursor: !isUserVerified ? 'not-allowed' : 'pointer',
              justifyContent: isDrawerExpanded ? 'flex-start' : 'center',
              backgroundColor: isRouteActive(dashboardItem.path) ? '#e3f2fd' : 'transparent',
              '&:hover': {
                backgroundColor: isRouteActive(dashboardItem.path) ? '#bbdefb' : '#f5f5f5',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: isDrawerExpanded ? '56px' : '0', justifyContent: 'center' }}>
              <img src="/images/dashboard.svg" alt="" style={{ width: '24px', height: '24px' }} />
            </ListItemIcon>
            <ListItemText 
              primary={dashboardItem.text}
              sx={{
                opacity: isDrawerExpanded ? 1 : 0,
                width: isDrawerExpanded ? 'auto' : 0,
                overflow: 'hidden',
                transition: 'opacity 0.3s ease, width 0.3s ease',
              }}
            />
          </ListItem>
        )}

        {showDashboard && menuItems.length > 0 && <Divider />}

        {/* Menu Items */}
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            disabled={!isUserVerified}
            onClick={() => {
              if (isUserVerified) {
                navigate(item.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              opacity: !isUserVerified ? 0.5 : 1,
              cursor: !isUserVerified ? 'not-allowed' : 'pointer',
              justifyContent: isDrawerExpanded ? 'flex-start' : 'center',
              backgroundColor: isRouteActive(item.path) ? '#e3f2fd' : 'transparent',
              '&:hover': {
                backgroundColor: isRouteActive(item.path) ? '#bbdefb' : '#f5f5f5',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: isDrawerExpanded ? '56px' : '0', justifyContent: 'center' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{
                opacity: isDrawerExpanded ? 1 : 0,
                width: isDrawerExpanded ? 'auto' : 0,
                overflow: 'hidden',
                transition: 'opacity 0.3s ease, width 0.3s ease',
              }}
            />
          </ListItem>
        ))}

      </List>
      
      {/* Pin Button at bottom */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 16, 
        left: 0, 
        right: 0, 
        display: 'flex', 
        justifyContent: 'center' 
      }}>
        <IconButton
          onClick={() => setIsDrawerPinned(!isDrawerPinned)}
          size="small"
          sx={{
            opacity: isDrawerExpanded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          {isDrawerPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
        </IconButton>
      </Box>
    </div>
  );

  const currentDrawerWidth = isDrawerExpanded ? drawerWidth : collapsedDrawerWidth;

  return (
    <Box sx={{ display: 'flex' , maxHeight: '100vh'}}>

      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mr: !isRtl ? { sm: `${currentDrawerWidth}px` } : 0,
          ml: isRtl ? { sm: `${currentDrawerWidth}px` } : 0,
          bgcolor: getSubscriptionColor(subscription?.plan),
          zIndex: (theme) => theme.zIndex.drawer + 2,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: '35px', sm: '64px' } }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className="dashboard-hamburger-menu"
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {t('common.appName')}
            </Typography>
            {/* <Box sx={{
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: 'transparent'
            }}>
              <div id="switch" ref={switchRef} style={{ 
                position: 'relative',
                transform: 'none',
                margin: 0
              }}>
                <div id="contentwrapper">
                  <div id="circle"></div>
                  <div id="stars">
                    <div className="star"></div>
                    <div className="star"></div>
                    <div className="star"></div>
                    <div id="shtngstarwrapper">
                      <div id="shootingstar"></div>
                    </div>
                  </div>
                  <div>
                    <div className="cloud">
                      <div className="cloudpart"></div>
                      <div className="cloudpart"></div> 
                    </div>
                    <div className="cloud">
                      <div className="cloudpart"></div>
                      <div className="cloudpart"></div> 
                    </div>
                    <div className="cloud">
                      <div className="cloudpart"></div>
                      <div className="cloudpart"></div>
                      <div className="cloudpart"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Box> */}
          </Stack>

          {/* Aida Ticketing button */}
          <Stack direction="row" sx={{gap:2}} alignItems="center" className='dashboard-buttons_video_info'>
            <IconButton
              onClick={() => window.open("https://app.didar.me/customer/form/ef9f2f24-93d7-4faf-8acc-e99ccaa11622", "_blank")}
              sx={{
                backgroundColor: getSubscriptionColor(subscription?.plan),
                color: 'white',
                zIndex: 1000,
                width: 35,
                height: 35,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              onClick={handleVideoButtonClick}
              className="dashboard-video-button"
              sx={{
                backgroundColor: getSubscriptionColor(subscription?.plan),
                color: 'white',
                zIndex: 1000,
                width: 35,
                height: 35,
              }}
            >
              <VideoLibraryIcon />
            </IconButton>
            {/* <IconButton
              onClick={handleHelpButtonClick}
              className="dashboard-help-button"
              sx={{
                backgroundColor: getSubscriptionColor(subscription?.plan),
                color: 'white',
                zIndex: 1000,
                width: 35,
                height: 35,
              }}
            >
              <HelpOutlineIcon />
            </IconButton> */}

            {/* Hide subscription info on xs screens */}
            <div className="dashboard-subscription-info" style={{display:'flex' , gap:'10px'}}>
            <Chip
              icon={<StarIcon />}
              label={subscription?.plan ? t(`onboarding.subscription.${subscription.plan}`) : t('onboarding.subscription.none')}
              color="default"
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
                display: { xs: 'none', sm: 'flex' },
              }}
            />
            {getRemainingDays() !== null && (
              <Chip
                label={`${getRemainingDays()} ${t('menu.daysRemaining')}`}
                color="default"
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
            )}
            </div>
            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleSignOut}
              className="dashboard-logout-button"
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {t('menu.logout')}
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        className="dashboard-sidebar"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          position: { sm: 'fixed' },
          right: !isRtl ? { sm: 0 } : 'auto',
          left: isRtl ? { sm: 0 } : 'auto',
          top: { sm: 0 },
          bottom: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
        onMouseEnter={handleDrawerMouseEnter}
        onMouseLeave={handleDrawerMouseLeave}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '80vw',
              minWidth: '180px',
              maxWidth: '320px',
              right: !isRtl ? 0 : 'auto',
              left: isRtl ? 0 : 'auto',
              zIndex: (theme) => theme.zIndex.drawer + 3, // Overlay AppBar on mobile
              top: 0,
              height: '100vh',
              position: 'fixed',
              overflowY: 'auto',
              direction: 'ltr', // Force LTR for scrollbar positioning
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
          anchor={isRtl ? 'left' : 'right'}
        >
          {mobileDrawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              right: !isRtl ? 0 : 'auto',
              left: isRtl ? 0 : 'auto',
              zIndex: (theme) => theme.zIndex.drawer,
              borderLeft: !isRtl ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
              borderRight: isRtl ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
              overflowY: 'auto',
              overflowX: 'hidden',
              direction: 'ltr', // Force LTR for scrollbar positioning
              transition: 'width 0.3s ease',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
          open
          anchor={isRtl ? 'left' : 'right'}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          position: 'relative',
          flexGrow: 1,
          p: 0,
          
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          mt: { xs: '55px', sm: '64px' }, // Ensure content is always below AppBar
          mr: !isRtl ? { xs: 0, sm: `${currentDrawerWidth}px` } : 0,
          ml: isRtl ? { xs: 0, sm: `${currentDrawerWidth}px` } : 0,
          maxWidth: '100vw',
          maxHeight: '90vh',
          overflowX: 'hidden',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth={false} sx={{p:0}}>
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;